/**
 * AI Recommendations Widget
 *
 * Proactively surfaces AI-generated insights and recommendations
 * on the dashboard without requiring user action.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  Server,
  Clock,
  ChevronRight,
  Lock,
  Zap,
  RefreshCw,
  Eye,
  ThumbsUp,
} from "lucide-react";
import { api } from "@/lib/api";

interface PendingClassification {
  id: string;
  identifier: string;
  sourceName: string;
  providerName: string | null;
  domainName: string;
  domainId: string;
  totalMessages: number;
  currentStatus: string;
  suggestedCategory: string | null;
  suggestedSubtype: string | null;
  aiClassification: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  analyzedAt: string;
  requiresApproval: boolean;
}

interface EnforcementInsight {
  id: string;
  domainId: string;
  domainName: string;
  canAdvance: boolean;
  confidence: string;
  reasoning: string;
  riskLevel: string;
  riskDescription: string;
  recommendations: string[];
  analyzedAt: string;
}

interface RecentAction {
  id: string;
  domainName: string;
  sourceName: string | null;
  sourceIdentifier: string | null;
  triggerType: string;
  actionTaken: string;
  classification: string;
  confidence: string;
  createdAt: string;
}

interface AIRecommendation {
  type: string;
  priority: "high" | "medium" | "low" | "critical";
  title: string;
  description: string;
  count: number;
  action: string;
  link: string;
  domains?: string[];
}

interface CachedInsight {
  id: string;
  domainId: string | null;
  sourceId: string | null;
  type: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  metadata: Record<string, unknown>;
  recommendations: string[];
  actionLink: string | null;
  confidence: string;
  createdAt: string;
}

interface AIRecommendationsData {
  available: boolean;
  tier: string;
  recommendations: AIRecommendation[];
  pendingClassifications: PendingClassification[];
  enforcementInsights: EnforcementInsight[];
  recentActions: RecentAction[];
  configIssues: Array<{
    sourceId: string;
    sourceName: string;
    domainName: string;
    issues: string[];
    recommendation: string;
  }>;
  insights?: CachedInsight[];
  generatedAt: string;
}

export function AIRecommendationsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState<AIRecommendationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  async function fetchRecommendations() {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAIRecommendations();
      setData(response);
    } catch (err) {
      console.error("Failed to fetch AI recommendations:", err);
      setError("Failed to load AI recommendations");
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(sourceId: string) {
    setProcessingAction(sourceId);
    try {
      await api.approveAIClassification(sourceId);
      // Remove from pending list
      if (data) {
        setData({
          ...data,
          pendingClassifications: data.pendingClassifications.filter(
            (c) => c.id !== sourceId
          ),
        });
      }
    } catch (err) {
      console.error("Failed to approve classification:", err);
    } finally {
      setProcessingAction(null);
    }
  }

  async function handleReject(sourceId: string) {
    // Navigate to source detail for manual classification
    navigate(`/sources?highlight=${sourceId}`);
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">AI Insights</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <RefreshCw className="w-5 h-5 text-purple-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">AI Insights</h3>
        </div>
        <p className="text-sm text-slate-500">{error}</p>
        <button
          onClick={fetchRecommendations}
          className="mt-2 text-sm text-purple-600 hover:text-purple-600 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  // Not available for free tier
  if (data && !data.available) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">AI Insights</h3>
          <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
            Premium
          </span>
        </div>
        <div className="flex items-start gap-3 mb-3">
          <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-slate-600 mb-2">
              Unlock AI-powered source analysis, automatic classifications, and enforcement recommendations.
            </p>
            <button
              onClick={() => navigate("/settings/billing")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Zap className="w-4 h-4" />
              Upgrade to Unlock
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No recommendations
  const hasContent =
    data &&
    (data.recommendations.length > 0 ||
      data.pendingClassifications.length > 0 ||
      data.recentActions.length > 0 ||
      (data.insights && data.insights.length > 0));

  if (!hasContent) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">AI Insights</h3>
        </div>
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 className="w-4 h-4" />
          <span>All caught up! No recommendations at this time.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">AI Insights</h3>
          {data && data.pendingClassifications.length > 0 && (
            <span className="px-1.5 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">
              {data.pendingClassifications.length}
            </span>
          )}
        </div>
        <button
          onClick={fetchRecommendations}
          className="p-1 text-slate-400 hover:text-purple-600 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Top Recommendations */}
      {data && data.recommendations.length > 0 && (
        <div className="space-y-2 mb-4">
          {data.recommendations.slice(0, 2).map((rec, idx) => (
            <button
              key={idx}
              onClick={() => navigate(rec.link)}
              className="w-full text-left p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {rec.priority === "critical" ? (
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    ) : rec.priority === "high" ? (
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    ) : rec.type === "enforcement_ready" ? (
                      <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Server className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    )}
                    <span className="font-medium text-slate-800 text-sm truncate">
                      {rec.title}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2">
                    {rec.description}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 flex-shrink-0 mt-0.5" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Cached Insights - Pre-computed findings */}
      {data && data.insights && data.insights.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Key Findings
            </span>
            <button
              onClick={() => navigate("/alerts")}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              View All ({data.insights.filter((i) => i.type !== "account_digest").length})
            </button>
          </div>
          <div className="space-y-2">
            {data.insights
              .filter((i) => i.type !== "account_digest")
              .slice(0, 5)
              .map((insight) => (
              <button
                key={insight.id}
                onClick={() => navigate("/alerts")}
                className="w-full text-left p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-2">
                  <InsightIcon type={insight.type} severity={insight.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-800 text-sm">
                        {insight.title}
                      </span>
                      <SeverityBadge severity={insight.severity} />
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {insight.description}
                    </p>
                    {insight.recommendations.length > 0 && (
                      <p className="text-xs text-purple-600 mt-1 font-medium">
                        {insight.recommendations[0]}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pending Classifications - Quick Actions */}
      {data && data.pendingClassifications.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Pending Approvals
            </span>
            <button
              onClick={() => navigate("/sources?filter=ai_pending")}
              className="text-xs text-purple-600 hover:text-purple-600 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {data.pendingClassifications.slice(0, 3).map((classification) => (
              <div
                key={classification.id}
                className="p-3 bg-white rounded-lg border border-purple-100"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 text-sm truncate">
                        {classification.sourceName}
                      </span>
                      <ConfidenceBadge confidence={classification.confidence} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {classification.domainName}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400">
                    {formatNumber(classification.totalMessages)} msgs
                  </span>
                </div>

                {/* AI Classification */}
                <div className="text-xs text-slate-600 mb-2 line-clamp-2">
                  <span className="font-medium">AI says:</span>{" "}
                  {formatClassification(classification.aiClassification)}
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApprove(classification.id)}
                    disabled={processingAction === classification.id}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded transition-colors disabled:opacity-50"
                  >
                    <ThumbsUp className="w-3 h-3" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(classification.id)}
                    disabled={processingAction === classification.id}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-medium rounded transition-colors disabled:opacity-50"
                  >
                    <Eye className="w-3 h-3" />
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent AI Actions */}
      {data && data.recentActions.length > 0 && (
        <div>
          <span className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2 block">
            Recent AI Activity
          </span>
          <div className="space-y-1">
            {data.recentActions.slice(0, 3).map((action) => (
              <div
                key={action.id}
                className="flex items-center gap-2 py-1.5 text-xs"
              >
                <ActionIcon action={action.actionTaken} />
                <span className="text-slate-600 truncate flex-1">
                  {formatActionDescription(action)}
                </span>
                <span className="text-slate-400 flex-shrink-0">
                  {formatRelativeTime(action.createdAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper components
function ConfidenceBadge({ confidence }: { confidence: "high" | "medium" | "low" }) {
  const colors = {
    high: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[confidence]}`}>
      {confidence}
    </span>
  );
}

function ActionIcon({ action }: { action: string }) {
  switch (action) {
    case "auto_classified":
      return <Sparkles className="w-3 h-3 text-purple-500 flex-shrink-0" />;
    case "alert_created":
      return <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />;
    case "classification_approved":
      return <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />;
    case "classification_rejected":
      return <XCircle className="w-3 h-3 text-red-600 flex-shrink-0" />;
    default:
      return <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />;
  }
}

function InsightIcon({ type, severity }: { type: string; severity: string }) {
  const iconClass = "w-4 h-4 flex-shrink-0";
  const colorClass = severity === "critical" ? "text-red-600" :
                     severity === "high" ? "text-orange-500" :
                     severity === "medium" ? "text-amber-500" :
                     "text-blue-500";

  switch (type) {
    case "provider_issue":
      return <Server className={`${iconClass} ${colorClass}`} />;
    case "spoofing_detected":
      return <XCircle className={`${iconClass} ${colorClass}`} />;
    case "authentication_issue":
      return <AlertTriangle className={`${iconClass} ${colorClass}`} />;
    case "forwarder_impact":
      return <Shield className={`${iconClass} text-blue-500`} />;
    default:
      return <Sparkles className={`${iconClass} ${colorClass}`} />;
  }
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-blue-100 text-blue-700",
    info: "bg-slate-100 text-slate-600",
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[severity] || colors.info}`}>
      {severity}
    </span>
  );
}

// Helper functions
function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
}

function formatClassification(classification: string): string {
  const labels: Record<string, string> = {
    legitimate_misconfigured: "Legitimate sender with configuration issues",
    legitimate_configured: "Legitimate, properly configured sender",
    suspicious: "Suspicious activity detected",
    likely_spoofing: "Likely spoofing attempt",
    forwarder: "Mail forwarder or mailing list",
    unknown: "Unable to determine classification",
  };
  return labels[classification] || classification;
}

function formatActionDescription(action: RecentAction): string {
  const source = action.sourceName || action.sourceIdentifier || "a source";
  switch (action.actionTaken) {
    case "auto_classified":
      return `Classified ${source} on ${action.domainName}`;
    case "alert_created":
      return `Created alert for ${source}`;
    case "classification_approved":
      return `Approved classification for ${source}`;
    case "classification_rejected":
      return `Updated classification for ${source}`;
    default:
      return `Analyzed ${source}`;
  }
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}